import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

interface MFAInputProps {
  onChange: (code: string) => void;
  error?: boolean;
  size?: 'sm' | 'lg';
  className?: string;
}

const MFAInput = ({
  onChange,
  error,
  size = 'lg',
  className = '',
}: MFAInputProps) => {
  const inputCount = 6;
  const [values, setValues] = useState<string[]>(Array(inputCount).fill(''));
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;

    const newValues = [...values];
    newValues[index] = value;
    setValues(newValues);
    onChange?.(newValues.join(''));

    // If user enters a digit, move to next input
    if (value && index < inputCount - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    const { key } = e;
    // Instantly overwrite value if a digit key is pressed
    if (/^\d$/.test(key)) {
      e.preventDefault();
      const newValues = [...values];
      newValues[index] = key;
      setValues(newValues);
      onChange?.(newValues.join(''));
      if (index < inputCount - 1) {
        inputsRef.current[index + 1]?.focus();
      }
      return;
    }
    if (key === 'Backspace') {
      e.preventDefault();
      const newValues = [...values];
      if (values[index]) {
        newValues[index] = '';
        setValues(newValues);
        onChange?.(newValues.join(''));
      } else if (index > 0) {
        inputsRef.current[index - 1]?.focus();
        newValues[index - 1] = '';
        setValues(newValues);
        onChange?.(newValues.join(''));
      }
    } else if (key === 'ArrowLeft' && index > 0) {
      inputsRef.current[index - 1]?.focus();
    } else if (key === 'ArrowRight' && index < inputCount - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  return (
    <div className={`${className} flex justify-center gap-x-4`}>
      {values.map((value, index) => (
        <input
          key={index}
          type='text'
          inputMode='numeric'
          maxLength={1}
          value={value}
          ref={(el) => (inputsRef.current[index] = el)}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          className={classNames(
            'h-8 w-8 rounded-md border text-center text-xl !leading-8 focus:outline-none focus:ring-2 focus:ring-dark',
            {
              'sm:h-10 sm:w-10': size === 'sm',
              'sm:h-16 sm:w-16': size === 'lg',
              'border-red-500': error,
            }
          )}
          data-testid={`mfa-input-${index}`}
        />
      ))}
    </div>
  );
};

export default MFAInput;
